import {
	ActionIcon,
	Button,
	CloseButton,
	Group,
	Paper,
	Stack,
	Text,
	Tooltip,
} from "@mantine/core";
import {
	IconMicrophone,
	IconMicrophoneOff,
	IconPlayerStop,
	IconVolume,
	IconVolumeOff,
} from "@tabler/icons-react";
import type { AssistantVoiceText } from "@/locales/assistant-voice";
import type { VoiceStatus } from "@/store/assistant-voice";
import { fillTemplate } from "../assistant.logic";
import { PV } from "../assistant-panel.tokens";
import { formatClock, voiceDisabledReason } from "./voice.logic";
import { VOICE_STRIP_GAP, VOICE_STRIP_MX } from "./voice-start.styles";
import { VoiceStartButton } from "./voice-start-button";

export interface VoiceControlsViewProps {
	t: AssistantVoiceText;
	name: string;
	status: VoiceStatus;
	supported: boolean;
	transcript: string;
	/** Kata pengisi GPT-Live giliran ini — baris abu-abu kecil, bukan jawaban. */
	filler: string;
	/** Sisa detik (sesi/jatah harian, mana yang lebih dulu); null = belum diketahui. */
	remaining: number | null;
	inviteExtend: boolean;
	answerMuted: boolean;
	micMuted: boolean;
	notice: string | null;
	onStart(): void;
	onStop(): void;
	onExtend(): void;
	onToggleAnswer(): void;
	onToggleMic(): void;
	onDismissNotice(): void;
}

const STATUS_COLOR: Record<Exclude<VoiceStatus, "off">, string> = {
	connecting: "gray",
	ready: "teal",
	listening: "blue",
	thinking: "indigo",
	answering: "violet",
};

function Notice({
	text,
	label,
	onClose,
}: {
	text: string;
	label: string;
	onClose(): void;
}) {
	return (
		<Group gap={4} wrap="nowrap" align="flex-start" role="status">
			<Text size="xs" c="orange" style={{ flex: 1 }}>
				{text}
			</Text>
			<CloseButton size="xs" aria-label={label} onClick={onClose} />
		</Group>
	);
}

/** Baris mode suara di atas composer: tombol On, atau status + kontrol saat aktif. */
export function VoiceControlsView(p: VoiceControlsViewProps) {
	const { t } = p;
	if (p.status === "off") {
		const reason = voiceDisabledReason(p.supported, t);
		const label = fillTemplate(t.start, { name: p.name });
		return (
			<Stack
				gap={6}
				mx={VOICE_STRIP_MX}
				mt={VOICE_STRIP_GAP}
				mb={VOICE_STRIP_GAP}
			>
				<VoiceStartButton
					label={label}
					hint={t.startHint}
					reason={reason}
					onClick={p.onStart}
				/>
				{reason ? (
					<Text size="xs" c="dimmed">
						{reason}
					</Text>
				) : null}
				{p.notice ? (
					<Notice
						text={p.notice}
						label={t.dismiss}
						onClose={p.onDismissNotice}
					/>
				) : null}
			</Stack>
		);
	}

	const MicIcon = p.micMuted ? IconMicrophoneOff : IconMicrophone;
	return (
		<Paper
			withBorder
			radius="md"
			mx={VOICE_STRIP_MX}
			mt={VOICE_STRIP_GAP}
			mb={VOICE_STRIP_GAP}
			px="sm"
			py={6}
			style={{ background: PV.stripBg, borderColor: PV.divider }}
		>
			<Stack gap={4}>
				<Group gap={6} wrap="nowrap" justify="space-between">
					<Group gap={6} wrap="nowrap">
						<MicIcon
							size={16}
							color={p.micMuted ? "gray" : "red"}
							aria-label={p.micMuted ? t.unmuteMic : t.micActive}
							data-voice-mic-indicator
						/>
						<Text
							size="sm"
							fw={600}
							c={STATUS_COLOR[p.status]}
							aria-live="polite"
							data-voice-status={p.status}
						>
							{t.status[p.status]}
						</Text>
						{p.remaining !== null ? (
							<Text size="xs" c="dimmed" data-voice-remaining>
								{fillTemplate(t.remaining, { waktu: formatClock(p.remaining) })}
							</Text>
						) : null}
					</Group>
					<Group gap={2} wrap="nowrap">
						<Tooltip label={p.answerMuted ? t.unmuteAnswer : t.muteAnswer}>
							<ActionIcon
								variant="subtle"
								color="gray"
								aria-label={p.answerMuted ? t.unmuteAnswer : t.muteAnswer}
								aria-pressed={p.answerMuted}
								onClick={p.onToggleAnswer}
							>
								{p.answerMuted ? (
									<IconVolumeOff size={16} />
								) : (
									<IconVolume size={16} />
								)}
							</ActionIcon>
						</Tooltip>
						<Tooltip label={p.micMuted ? t.unmuteMic : t.muteMic}>
							<ActionIcon
								variant="subtle"
								color="gray"
								aria-label={p.micMuted ? t.unmuteMic : t.muteMic}
								aria-pressed={p.micMuted}
								onClick={p.onToggleMic}
							>
								<MicIcon size={16} />
							</ActionIcon>
						</Tooltip>
						<Tooltip label={t.stop}>
							<ActionIcon
								variant="light"
								color="red"
								aria-label={t.stop}
								onClick={p.onStop}
								data-voice-stop
							>
								<IconPlayerStop size={16} />
							</ActionIcon>
						</Tooltip>
					</Group>
				</Group>
				{p.transcript ? (
					<Text size="xs" fs="italic" c="dimmed" lineClamp={2}>
						{p.transcript}
					</Text>
				) : null}
				{p.filler ? (
					<Text size="xs" c="dimmed" lineClamp={1} data-voice-filler>
						<Text span size="xs" fw={600} inherit data-voice-filler-label>
							{fillTemplate(t.fillerLabel, { name: p.name })}
						</Text>{" "}
						{p.filler}
					</Text>
				) : null}
				{p.inviteExtend ? (
					<Group gap={6} wrap="nowrap" justify="space-between">
						<Text size="xs">{t.extendInvite}</Text>
						<Button size="compact-xs" variant="light" onClick={p.onExtend}>
							{t.extend}
						</Button>
					</Group>
				) : null}
				{p.notice ? (
					<Notice
						text={p.notice}
						label={t.dismiss}
						onClose={p.onDismissNotice}
					/>
				) : null}
			</Stack>
		</Paper>
	);
}
